import { useCallback } from 'react'
import type { useI18n } from '../../../i18n'
import {
  requestPasskeys,
  requestPasskeyRegisterOptions,
  requestPasskeyRegisterVerify,
  requestRemovePasskey,
  type PasskeyCredential,
} from '../passkeyApi'
import { createPasskeyRegistrationResponse } from './passkeyWebauthn'
import type { AdminHeadersGetter } from './types'

type Params = {
  authHeaders: AdminHeadersGetter
  t: ReturnType<typeof useI18n>['t']
  ensureAuthenticated: () => boolean
}

export const useProfileSecurityPasskeyActions = ({
  authHeaders,
  t,
  ensureAuthenticated,
}: Params) => {
  const loadPasskeys = useCallback(async (): Promise<PasskeyCredential[]> => {
    if (!ensureAuthenticated()) return []
    return requestPasskeys({ authHeaders, t })
  }, [authHeaders, ensureAuthenticated, t])

  const registerPasskey = useCallback(
    async (nickname?: string): Promise<PasskeyCredential[]> => {
      if (!ensureAuthenticated()) return []

      const options = await requestPasskeyRegisterOptions({ authHeaders, t })
      const registrationResponse = await createPasskeyRegistrationResponse(options)
      await requestPasskeyRegisterVerify({
        authHeaders,
        response: registrationResponse,
        nickname,
        t,
      })

      return requestPasskeys({ authHeaders, t })
    },
    [authHeaders, ensureAuthenticated, t],
  )

  const removePasskey = useCallback(
    async (passkeyId: string): Promise<PasskeyCredential[]> => {
      if (!ensureAuthenticated()) return []
      await requestRemovePasskey({ authHeaders, passkeyId, t })
      return requestPasskeys({ authHeaders, t })
    },
    [authHeaders, ensureAuthenticated, t],
  )

  return {
    loadPasskeys,
    registerPasskey,
    removePasskey,
  }
}
